import { useQuery } from 'react-query'
import { 
  Bot, 
  Phone, 
  Settings, 
  PhoneCall, 
  TrendingUp, 
  Clock,
  Users,
  Activity
} from 'lucide-react'
import { callsAPI, agentsAPI, phoneNumbersAPI, appsAPI } from '../services/api'
import LoadingSpinner from '../components/LoadingSpinner'
import StatsCard from '../components/StatsCard'
import RecentCalls from '../components/RecentCalls'
import CallChart from '../components/CallChart'

function Dashboard() {
  const { data: callStats, isLoading: statsLoading } = useQuery(
    'call-stats',
    () => callsAPI.getCallStats(),
    { refetchInterval: 30000 }
  )

  const { data: agents, isLoading: agentsLoading } = useQuery(
    'agents',
    agentsAPI.getAgents
  )

  const { data: phoneNumbers, isLoading: phonesLoading } = useQuery(
    'phone-numbers',
    phoneNumbersAPI.getPhoneNumbers
  )

  const { data: apps, isLoading: appsLoading } = useQuery(
    'apps',
    appsAPI.getApps
  )

  const { data: recentCalls, isLoading: callsLoading } = useQuery(
    'recent-calls',
    () => callsAPI.getCalls({ limit: 10 })
  )

  const { data: callTimeline } = useQuery(
    'call-timeline',
    () => callsAPI.getCallTimeline({ 
      dateFrom: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
      interval: 'day'
    })
  )

  if (statsLoading || agentsLoading || phonesLoading || appsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    )
  }

  const stats = callStats?.data?.summary || {}
  const statusBreakdown = callStats?.data?.statusBreakdown || []

  const quickStats = [
    {
      name: 'Total Calls',
      value: stats.totalCalls || 0,
      icon: PhoneCall,
      change: '+12%',
      changeType: 'positive'
    },
    {
      name: 'Active Agents',
      value: agents?.data?.agents?.filter(a => a.isActive).length || 0,
      icon: Bot,
      change: '+2',
      changeType: 'positive'
    },
    {
      name: 'Phone Numbers',
      value: phoneNumbers?.data?.phoneNumbers?.length || 0,
      icon: Phone,
      change: '0',
      changeType: 'neutral'
    },
    {
      name: 'Apps',
      value: apps?.data?.apps?.length || 0,
      icon: Settings,
      change: '+1',
      changeType: 'positive'
    }
  ]

  const completionRate = stats.totalCalls > 0 
    ? ((stats.completedCalls / stats.totalCalls) * 100).toFixed(1)
    : 0

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold text-primary-900">Dashboard</h1>
        <p className="mt-1 text-sm text-primary-600">
          Overview of your AI phone system performance and activity
        </p>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {quickStats.map((stat) => (
          <StatsCard key={stat.name} {...stat} />
        ))}
      </div>

      {/* Additional Metrics */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <TrendingUp className="h-8 w-8 text-green-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-primary-500 truncate">
                    Call Completion Rate
                  </dt>
                  <dd className="text-lg font-medium text-primary-900">
                    {completionRate}%
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Clock className="h-8 w-8 text-blue-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-primary-500 truncate">
                    Avg Call Duration
                  </dt>
                  <dd className="text-lg font-medium text-primary-900">
                    {Math.round(stats.averageDuration || 0)}s
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Activity className="h-8 w-8 text-purple-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-primary-500 truncate">
                    Total Duration
                  </dt>
                  <dd className="text-lg font-medium text-primary-900">
                    {Math.round((stats.totalDuration || 0) / 60)}m
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Charts and Recent Activity */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Call Timeline Chart */}
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-medium text-primary-900">
              Call Activity (Last 7 Days)
            </h3>
          </div>
          <div className="card-body">
            {callTimeline ? (
              <CallChart data={callTimeline.data.timeline} />
            ) : (
              <div className="h-64 flex items-center justify-center">
                <LoadingSpinner />
              </div>
            )}
          </div>
        </div>

        {/* Recent Calls */}
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-medium text-primary-900">
              Recent Calls
            </h3>
          </div>
          <div className="card-body">
            {callsLoading ? (
              <div className="flex items-center justify-center h-32">
                <LoadingSpinner />
              </div>
            ) : (
              <RecentCalls calls={recentCalls?.data?.calls || []} />
            )}
          </div>
        </div>
      </div>

      {/* Call Status Breakdown */}
      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-medium text-primary-900">
            Call Status Breakdown
          </h3>
        </div>
        <div className="card-body">
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            {statusBreakdown.map((status) => (
              <div key={status._id} className="text-center">
                <div className="text-2xl font-bold text-primary-900">
                  {status.count}
                </div>
                <div className="text-sm text-primary-600 capitalize">
                  {status._id || 'Unknown'}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

export default Dashboard